import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { Button, Icon, Pill, PillCluster, Tabs, TopBar } from 'admination-react'
import { orgSettingsTabs } from '@/core/templates'
import type { AppView, OrgSettingsTab } from '@/core/state/appStore'
import { tabsPillProps, type TabsPillItem } from './TabsPill'
import { AccountLabel, AccountSignOut, OrganisationSwitcher, UserMenu } from './UserMenu'
import { useClusterSwap } from '@/core/hooks/useClusterSwap'
import { useCompactActions } from '@/core/hooks/useCompactActions'

type Click = (event: React.MouseEvent<HTMLElement>) => void
export const staffDestinations = [
  { view: 'dashboard', icon: 'i-coaching', text: 'Coachings', action: 'open-coachings-view' },
  { view: 'coaches', icon: 'i-people', text: 'Coaches', action: 'open-coaches-view' },
  { view: 'massnahmen', icon: 'i-massnahme', text: 'Maßnahmen', action: 'open-massnahmen-view' },
  { view: 'module', icon: 'i-module', text: 'Module', action: 'open-module-view' },
  { view: 'anwesenheit', icon: 'i-calendar', text: 'Anwesenheit', action: 'open-anwesenheit-view' },
] as const

function NavButton({ action, icon, text, title, primary = false, active, busy, compact, onClick }: {
  action: string
  icon: string
  text?: string
  title?: string
  primary?: boolean
  active?: boolean
  busy?: boolean
  compact: boolean
  onClick: Click
}) {
  const iconOnly = !primary && (!text || compact)
  const hint = iconOnly ? title ?? text : title
  return <div className="pill cluster-enter">
    <Button
      type="button"
      variant={primary ? 'success' : 'default'}
      primaryAction={primary}
      iconOnly={!!iconOnly}
      isActive={active}
      data-action={action}
      title={hint}
      aria-label={hint}
      loading={busy}
      onClick={onClick}
    >
      <Icon name={icon} />{!iconOnly && text}
    </Button>
  </div>
}

export function Navbar({
  view, allowedViews = [], onNavigate, onEntry, onReset, onTeilnehmersicht, onInvite, onCreateMassnahme, onCreateModul, onAnleitung,
  tools, centerExtra, rightExtra, entry, entryBusy, entryActive,
  onOpenSettings, onOpenMeineEinstellungen, orgSettingsTab, onOrgSettingsTabChange, orgSettingsTabIds,
  onClose, personalSettings = false, inert = false,
}: {
  view?: AppView
  allowedViews?: readonly string[]
  onNavigate?: (view: AppView) => void
  onEntry?: Click
  onReset?: Click
  onTeilnehmersicht?: Click
  onInvite?: Click
  onCreateMassnahme?: Click
  onCreateModul?: Click
  onAnleitung?: Click
  tools?: ReactNode
  centerExtra?: ReactNode
  rightExtra?: ReactNode
  entry?: { label: string; action: string }
  entryBusy?: boolean
  entryActive?: boolean
  onOpenSettings?: () => void
  onOpenMeineEinstellungen?: () => void
  orgSettingsTab?: OrgSettingsTab
  onOrgSettingsTabChange?: (tab: OrgSettingsTab) => void
  orgSettingsTabIds?: readonly string[]
  onClose?: () => void
  personalSettings?: boolean
  inert?: boolean
}) {
  const toolsSwap = useClusterSwap(tools, tools != null)

  const rightExtraSwap = useClusterSwap(rightExtra, !!rightExtra)
  const barRef = useRef<HTMLElement>(null)
  const toolPositions = useRef<Map<Element, DOMRect>>(new Map())
  const toolSurface = orgSettingsTab ?? (view && `open-${view === 'dashboard' ? 'coachings' : view}-view`)
  const previousTab = useRef(toolSurface)
  useLayoutEffect(() => {
    const elements = barRef.current?.querySelectorAll('.bar-center > .pill-cluster > .cluster-enter') ?? []
    const next = new Map<Element, DOMRect>()
    const motion = barRef.current && getComputedStyle(barRef.current)
    const duration = parseFloat(motion?.getPropertyValue('--dur-base') ?? '') || 250
    const easing = motion?.getPropertyValue('--ease-out').trim() || 'ease-out'
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const changingTab = !!previousTab.current && !!toolSurface && previousTab.current !== toolSurface
    previousTab.current = toolSurface
    for (const element of elements) {
      const rect = element.getBoundingClientRect()
      next.set(element, rect)
      const previous = toolPositions.current.get(element)
      if (changingTab && !reduced && previous && element.animate && (previous.x !== rect.x || previous.y !== rect.y)) {
        element.animate([{ transform: `translate(${previous.x - rect.x}px, ${previous.y - rect.y}px)` }, { transform: 'translate(0, 0)' }], { duration, easing })
      }
    }
    toolPositions.current = next
  })
  const compact = useCompactActions(barRef, [
    onReset && 'Zurücksetzen', onTeilnehmersicht && 'Teilnehmersicht', onEntry && entry?.label, onInvite && 'Coach hinzufügen',
    onCreateMassnahme && 'Maßnahme', onCreateModul && 'Modul',
  ].filter(Boolean).join('\n'))
  const navigation: TabsPillItem[] = orgSettingsTab || personalSettings ? [] : staffDestinations
    .filter((item) => item.view === 'dashboard' || allowedViews.includes(item.view))
    .map((item) => ({ key: item.action, icon: item.icon, text: item.text, title: item.text,
      action: item.action, isActive: item.view === view, onClick: () => onNavigate?.(item.view) }))
  if (orgSettingsTab && onOrgSettingsTabChange) navigation.push(...orgSettingsTabs
    .filter((tab) => !orgSettingsTabIds || orgSettingsTabIds.includes(tab.id))
    .map((tab) => ({ key: tab.id, icon: tab.icon, text: tab.label, title: tab.label,
      isActive: tab.id === orgSettingsTab, onClick: () => onOrgSettingsTabChange(tab.id as OrgSettingsTab) })))
  const anchored = rightExtraSwap.isShown && !!onEntry && !!entry
  return <TopBar
    ref={barRef}
    className={onClose ? 'bar--responsive bar--navigation modal-head' : 'bar--responsive bar--navigation'}
    data-navbar
    inert={inert}
    close={onClose ? { label: 'Schließen' } : undefined}
    start={!onClose ? <UserMenu onOpenSettings={onOpenSettings} onOpenMeineEinstellungen={onOpenMeineEinstellungen} />
      : orgSettingsTab || personalSettings ? <AccountLabel organisation={!!orgSettingsTab} /> : undefined}
    center={<PillCluster>
      {personalSettings ? <Pill><span className="btn btn--static is-active"><Icon name="i-person" /><span className="bar-title">Meine Einstellungen</span></span></Pill>
        : <Tabs {...tabsPillProps(navigation)} />}
      {toolsSwap.node}{centerExtra}
    </PillCluster>}
    right={<PillCluster>
      {personalSettings && <AccountSignOut />}
      {orgSettingsTab === 'organisation' && <OrganisationSwitcher />}
      {!anchored ? rightExtraSwap.node : null}
      {onReset && <NavButton action="zuruecksetzen" icon="i-arrow-left" text="Zurücksetzen" compact={compact} onClick={onReset} />}
      {onTeilnehmersicht && <NavButton action="teilnehmersicht-starten" icon="i-signature" text="Teilnehmersicht" title="Teilnehmersicht starten" compact={compact} onClick={onTeilnehmersicht} />}
      {onEntry && entry && <>
        {anchored ? rightExtraSwap.node : null}
        <NavButton action={entry.action} icon="i-chat" text={entry.label} primary={entry.action === 'open-erstgespraech'}
          active={entryActive} busy={!!entryBusy} compact={compact} onClick={onEntry} />
      </>}
      {onInvite && <NavButton action="invite-coach" icon="i-plus" text="Coach hinzufügen" primary compact={compact} onClick={onInvite} />}
      {onCreateMassnahme && <NavButton action="create-massnahme" icon="i-plus" text="Maßnahme" primary compact={compact} onClick={onCreateMassnahme} />}
      {onCreateModul && <NavButton action="create-modul" icon="i-plus" text="Modul" primary compact={compact} onClick={onCreateModul} />}
      {onAnleitung && <NavButton action="open-anleitung" icon="i-external" title="In neuem Tab öffnen" compact={compact} onClick={onAnleitung} />}
    </PillCluster>}
  />
}
